import { BehaviorSubject, EMPTY, filter, switchMap, take } from 'rxjs';
import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SubscriptionService } from '../../../services/subscription.service';
import { SUBSCRIPTION_PLAN, SUBSCRIPTION_STATUS } from '../../../enums';
import { SubscriptionDto } from '../../../interfaces';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../../../environments/environment';
import { CompanyService } from '../../../services/company.service';

@Component({
  selector: 'app-plans-list',
  templateUrl: './plans-list.component.html',
  styleUrls: ['./plans-list.component.scss']
})
export class PlansListComponent implements OnInit {
  @Input({ required: true }) companyId!: number;
  @Input() discount?: number;

  protected readonly SUBSCRIPTION_PLAN = SUBSCRIPTION_PLAN;
  protected readonly planList = [
    {
      name: SUBSCRIPTION_PLAN.SMALL,
      image: './assets/images/plan/small.png',
      price: 2000,
    },
    {
      name: SUBSCRIPTION_PLAN.MEDIUM,
      image: './assets/images/plan/medium.png',
      price: 4000
    }
  ];
  protected currentSubscription$: BehaviorSubject<SubscriptionDto | null> = new BehaviorSubject<SubscriptionDto | null>(null);

  constructor(
    private subscriptionService: SubscriptionService,
    private companyService: CompanyService,
    private dialogService: MatDialog,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.subscriptionService
      .getSubscriptionByCompanyId(this.companyId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((sub) => sub?.status === SUBSCRIPTION_STATUS.SUBSCRIBED)
      )
      .subscribe(this.currentSubscription$);
  }

  private initSubscription(plan: SUBSCRIPTION_PLAN, companyId: number) {
    this.subscriptionService.initPaymentRequest(plan, `${environment.workflow}/company/${companyId}/subscription?subscribed=success`).subscribe({
      next: (response) => {
        if (!response.success) {
          return;
        }

        window.open(response.data!, '_blank');
      }
    });
  }

  getPriceWithDiscount(price: number) {
    const discount = this.discount ?? 0;
    return price - price * (discount / 100);
  }

  async upgradePlan(plan: SUBSCRIPTION_PLAN): Promise<void> {
    const currentSubscription = this.currentSubscription$.getValue();
    const billing = this.companyService.companyInfo$.getValue()?.billing;

    if (!currentSubscription || !billing) {
      return;
    }

    const { SubscriptionUpgradeConfirmationComponent } = await import(
      '../../modals/subscription/subscription-upgrade-confirmation/subscription-upgrade-confirmation.component'
    );
    const { SubscriptionUpgradedModalComponent } = await import('../../modals/subscription/subscription-upgraded-modal/subscription-upgraded-modal.component');

    this.dialogService.closeAll();
    const upgradeDialogRef = this.dialogService.open(SubscriptionUpgradeConfirmationComponent, {
      panelClass: ['dialog-overlay-pane'],
      width: '500px',
      data: {
        nextChargeDate: billing.nextPaymentDueAt || '',
        planName: plan
      }
    });

    upgradeDialogRef
      .afterClosed()
      .pipe(
        take(1),
        switchMap((result: unknown) => {
          if (result !== 'confirm') {
            return EMPTY;
          }

          return this.subscriptionService.updateSubscriptionRequest(plan);
        })
      )
      .subscribe((response) => {
        if (!response.success) {
          return;
        }

        this.dialogService.closeAll();
        this.dialogService.open(SubscriptionUpgradedModalComponent, {
          panelClass: ['dialog-overlay-pane'],
          width: '500px',
          data: {
            nextChargeDate: billing.nextPaymentDueAt || ''
          }
        });
      });
  }

  async downgradePlan(plan: SUBSCRIPTION_PLAN): Promise<void> {
    const currentSubscription = this.currentSubscription$.getValue();
    const billing = this.companyService.companyInfo$.getValue()?.billing;

    if (!currentSubscription || !billing) {
      return;
    }

    const { SubscriptionDowngradeConfirmationComponent } = await import(
      '../../modals/subscription/subscription-downgrade-confirmation/subscription-downgrade-confirmation.component'
    );
    const { SubscriptionDowngradedModalComponent } = await import(
      '../../modals/subscription/subscription-downgraded-modal/subscription-downgraded-modal.component'
    );

    this.dialogService.closeAll();
    const downgradeDialogRef = this.dialogService.open(SubscriptionDowngradeConfirmationComponent, {
      panelClass: ['dialog-overlay-pane'],
      width: '500px',
      data: {
        nextChargeDate: billing.nextPaymentDueAt || '',
        planName: plan
      }
    });

    downgradeDialogRef
      .afterClosed()
      .pipe(
        take(1),
        switchMap((result: unknown) => {
          if (result !== 'confirm') {
            return EMPTY;
          }

          return this.subscriptionService.updateSubscriptionRequest(plan);
        })
      )
      .subscribe((response) => {
        if (!response.success) {
          return;
        }

        this.dialogService.closeAll();
        this.dialogService.open(SubscriptionDowngradedModalComponent, {
          panelClass: ['dialog-overlay-pane'],
          width: '500px',
          data: {
            nextChargeDate: billing.nextPaymentDueAt || ''
          }
        });
      });
  }

  async onSubscriptionPlanSelect(plan: SUBSCRIPTION_PLAN): Promise<void> {
    const currentSubscription = this.currentSubscription$.getValue();

    if (!currentSubscription) {
      // it's a first subscription plan
      return this.initSubscription(plan, this.companyId);
    }

    const planComparison = this.subscriptionService.compareSubscriptionPlans(currentSubscription.plan.name, plan);

    if (planComparison === 0) {
      // the selected plan is the same as the current plan
      return;
    }

    if (planComparison === 1) {
      await this.downgradePlan(plan);
      return;
    }

    if (planComparison === -1) {
      await this.upgradePlan(plan);
      return;
    }
  }

  comparePlans(planA: SUBSCRIPTION_PLAN, planB: SUBSCRIPTION_PLAN) {
    return this.subscriptionService.compareSubscriptionPlans(planA, planB);
  }
}
